import React, { useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { RxCross2 } from 'react-icons/rx';
import { Separator } from '@radix-ui/react-separator';
import { useDialogMoreInfoDocumentStore } from '../../store/dialogs.store';
import { useLocalStorage } from '../../../../../hooks/useLocalStorage';
import * as Yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { SubmitHandler, useForm } from 'react-hook-form';
import { DocumentService } from '../../../../../services/DocumentService';
import { useTranslation } from 'react-i18next';
import { CustomInput } from '../../../../../components/inputs';
import { errorToast } from '../../../../../utils/toasts';
import { errorMessageAdapter } from '../../../../../adapters';
import { AxiosError, AxiosInstance } from 'axios';
import { MainCustomButton } from '../../../../../components/Buttons';
import { useApi } from '../../../../../services/hooks/apiHook';

interface IMoreInfo {
  reason: string;
  complementary: string;
}

const DialogMoreInfo = ({ shareholderSlug, documentSlug, onDocumentApproved }: any) => {
  const { open: openMoreInfo, setOpen: setOpenMoreInfo }: any = useDialogMoreInfoDocumentStore();
  const api: AxiosInstance = useApi();
  const { t, i18n } = useTranslation();
  const [token] = useLocalStorage('access-token', null);
  const [loading, setLoading] = useState(false);
  const validationSchema = Yup.object().shape({
    reason: Yup.string().required('Required'),
    complementary: Yup.string().required('Required')
  });
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors }
  } = useForm<IMoreInfo>({
    resolver: yupResolver(validationSchema)
  });

  const moreInfoDocument: SubmitHandler<IMoreInfo> = async (data: IMoreInfo) => {
    try {
      setLoading(true);
      await DocumentService.moreInfoDocument(
        api,
        {
          shareholder_slug: shareholderSlug,
          document_slug: documentSlug,
          subject: data.reason,
          message: data.complementary
        },
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Language': i18n.language.toLowerCase()
          }
        }
      );
      setLoading(false);
    } catch (e) {
      setLoading(false);
      errorToast(errorMessageAdapter(e as AxiosError));
    }
    setOpenMoreInfo(false);
    reset();
    onDocumentApproved();
  };

  return (
    <Dialog.Root open={openMoreInfo} onOpenChange={setOpenMoreInfo}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-20 data-[state=open]:animate-overlayShow" />
        <Dialog.Content className="fixed left-[50%] top-[50%] max-h-[85vh] w-[90vw] max-w-[500px] translate-x-[-50%] translate-y-[-50%] rounded-[6px] bg-white py-6 shadow-[hsl(206_22%_7%_/_35%)_0px_10px_38px_-10px,_hsl(206_22%_7%_/_20%)_0px_10px_20px_-15px] focus:outline-none data-[state=open]:animate-contentShow">
          <Dialog.Title className="m-0 text-[17px] font-medium text-black">
            <h1 className="px-4">{t('others.othModalRejected')}</h1>
            <Separator className="my-[15px] bg-slate-200 data-[orientation=horizontal]:h-px data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px" />
          </Dialog.Title>
          <form onSubmit={handleSubmit(moreInfoDocument)}>
            <div className="px-4">
              <CustomInput
                id="reason"
                label={t('labels.lblReasonForMoreInfo')}
                type="text"
                error={errors.reason?.message}
                register={register}
              />
              <div className="my-6 flex w-full flex-col gap-1">
                <div className="flex flex-row items-center gap-3">
                  <label className={`${errors.complementary ? 'text-red-500' : 'text-[#0C1FAA]'} text-sm `}>
                    {t('others.othComplementaryMessage')}
                  </label>
                </div>
                <textarea
                  className={`${
                    errors.complementary ? 'border-red-500' : 'border-[#CED4DA]'
                  } h-[40px] w-full rounded-lg border-[1px] px-4 text-sm outline-none focus:border-blue-700`}
                  {...register('complementary')}
                  placeholder={''}
                />
                <span className="h-0 text-xs text-red-500">{errors.complementary?.message}</span>
              </div>
            </div>
            <Separator className="my-[15px] bg-slate-200 data-[orientation=horizontal]:h-px data-[orientation=vertical]:h-full data-[orientation=horizontal]:w-full data-[orientation=vertical]:w-px" />
            <div className="mt-[25px] grid grid-cols-3 gap-8 px-4">
              <Dialog.Close asChild>
                <button className="flex items-center justify-center rounded-md border border-[#0C1FAA] text-sm font-medium text-[#0C1FAA]">
                  {t('others.othCancel')}
                </button>
              </Dialog.Close>
              <MainCustomButton text={t('others.othSave')} type="submit" loading={loading} />
            </div>
          </form>
          <Dialog.Close asChild>
            <button
              className="absolute right-[10px] top-[10px] inline-flex h-[25px] w-[25px] appearance-none items-center justify-center rounded-full text-violet11 hover:bg-violet4 focus:shadow-[0_0_0_2px] focus:shadow-violet7 focus:outline-none"
              aria-label="Close"
            >
              <RxCross2 />
            </button>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default DialogMoreInfo;
